// src/components/Sonde.jsx — LA SONDE, écran de l'éditeur.
//
// Une question : « est-ce qu'ERPIA marche encore ? » — et, quand la réponse
// est non, « depuis quand ? ». La sonde (serveur : utils/sonde.js) rejoue les
// gestes qui comptent dans sa propre organisation de test et efface tout
// derrière elle ; cet écran la lance et lit ses comptes rendus.
//
// Partis pris :
// 1. L'ÉLÉMENT SIGNATURE est le RUBAN : un trait par passage, du plus ancien
//    au plus récent. Le vert est la norme et reste discret ; le rouge se voit
//    avant qu'on lise, et la longueur de la traînée rouge dit depuis quand.
// 2. LE DERNIER PASSAGE SE LIT CONTRÔLE PAR CONTRÔLE, en lignes dépliables :
//    ce qui est en échec s'ouvre tout seul, ce qui passe reste fermé.
// 3. « RIEN N'EST RESTÉ » est dit à chaque passage, en chiffres : c'est la
//    promesse de la sonde, elle se vérifie à l'écran.
// 4. PENDANT UN PASSAGE, TOUS LES CONTRÔLES SONT À L'ÉCRAN : faits, en cours
//    (ses vérifications apparaissent une à une), en attente. On voit ce qui
//    tourne et ce qui reste, pas une roue qui tourne.
// 5. LA COUVERTURE dit ce que la sonde n'appelle JAMAIS, calculée par le
//    serveur sur les appels réels du dernier passage : c'est la liste de ce
//    qu'il reste à tester, modules les moins éprouvés d'abord.
// 6. LES TESTS AJOUTÉS (scénarios) s'écrivent dans la langue du copilote :
//    une suite de ses propres actions. On les lui demande en conversation ;
//    ici on les relit, on les essaie, on les adopte. Un test « à l'essai »
//    tourne sans faire passer la sonde au rouge.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import APIService from '../services/api';
import { confirmErpia } from './ConfirmDialog';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon }) => <i className={`bi bi-${icon}`} aria-hidden="true" />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });

const STATUTS = {
  ok: { libelle: 'Passe', classe: 'ok' },
  avertissement: { libelle: 'À regarder', classe: 'warn' },
  echec: { libelle: 'En échec', classe: 'bad' },
  erreur: { libelle: 'Interrompu', classe: 'bad' },
  en_cours: { libelle: 'En cours', classe: 'run' },
  attente: { libelle: 'En attente', classe: 'wait' },
};
const ETAPES = {
  preparation: 'Préparation de l\'espace de test…',
  controles: null,
  balayage: 'Effacement des données de test…',
};
const METHODES = { GET: 'lecture', POST: 'création', PUT: 'modification', PATCH: 'modification', DELETE: 'suppression' };
const statut = s => STATUTS[s] || { libelle: s || '—', classe: 'run' };

const date = iso => (iso ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
const DECLENCHEURS = { manuel: 'lancé à la main', deploiement: 'après un déploiement', planifie: 'passage planifié', automatisation: 'lancé par l\'automatisation', essai: 'essai d\'un test' };
const ETATS_TEST = { essai: 'À l\'essai', adopte: 'Adopté', desactive: 'Désactivé' };

async function lireJson(r) {
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j.success === false) throw new Error(j.error || `Erreur ${r.status}`);
  return j;
}

export default function Sonde({ showNotification }) {
  const [vue, setVue] = useState(null);          // null = chargement | { passages } | { absent } | { erreur }
  const [detail, setDetail] = useState(null);    // passage complet affiché
  const [ouverts, setOuverts] = useState({});    // id contrôle -> bool (surcharge du défaut)
  const [lancement, setLancement] = useState(false);
  const [couv, setCouv] = useState(null);        // null | { modules } | { erreur } | { absent }
  const [couvOuverte, setCouvOuverte] = useState(null); // module déplié
  const [couvToutes, setCouvToutes] = useState(false);
  const [tests, setTests] = useState(null);      // null | [] | { erreur } | { absent }
  const [testOuvert, setTestOuvert] = useState(null);
  const [testBusy, setTestBusy] = useState(null); // `${id}:${geste}`
  const [essais, setEssais] = useState({});      // id -> résultat du dernier essai
  const minuterie = useRef(null);

  const chargerTests = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/sonde/scenarios`, { headers: authH() });
      if (r.status === 404 || r.status === 501) { setTests({ absent: true }); return; }
      const j = await lireJson(r);
      setTests(j.data || []);
    } catch (e) { setTests({ erreur: e.message }); }
  }, []);

  const chargerCouverture = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/sonde/couverture`, { headers: authH() });
      if (r.status === 404 || r.status === 501) { setCouv({ absent: true }); return; }
      const j = await lireJson(r);
      setCouv(j.data);
    } catch (e) { setCouv({ erreur: e.message }); }
  }, []);

  const chargerDetail = useCallback(async (id) => {
    try {
      const r = await fetch(`${BASE}/sonde/passages/${id}`, { headers: authH() });
      const j = await lireJson(r);
      setDetail(j.data);
      return j.data;
    } catch (e) { setDetail({ erreur: e.message }); return null; }
  }, []);

  const charger = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/sonde/passages?limit=30`, { headers: authH() });
      if (r.status === 404 || r.status === 501) { setVue({ absent: true }); return; }
      if (r.status === 403) { setVue({ interdit: true }); return; }
      const j = await lireJson(r);
      setVue({ passages: j.data || [] });
      if (j.data?.length) chargerDetail(j.data[0].id);
      else setDetail(null);
      chargerCouverture();
      chargerTests();
    } catch (e) { setVue({ erreur: e.message }); }
  }, [chargerDetail, chargerCouverture, chargerTests]);
  useEffect(() => { charger(); }, [charger]);

  // Un passage en cours se suit : le compte rendu se remplit contrôle par
  // contrôle, et la liste est relue quand il se termine.
  useEffect(() => {
    clearTimeout(minuterie.current);
    if (detail?.statut === 'en_cours') {
      minuterie.current = setTimeout(async () => {
        const d = await chargerDetail(detail.id);
        if (d && d.statut !== 'en_cours') { charger(); }
      }, 2000);
    }
    return () => clearTimeout(minuterie.current);
  }, [detail, chargerDetail, charger]);

  const lancer = async () => {
    setLancement(true);
    try {
      const r = await fetch(`${BASE}/sonde/lancer`, { method: 'POST', headers: { ...authH(), 'Content-Type': 'application/json' }, body: '{}' });
      const j = await r.json().catch(() => ({}));
      if (r.status === 409 && j.passage_id) { chargerDetail(j.passage_id); showNotification?.(j.error, 'info'); return; }
      if (!r.ok || j.success === false) throw new Error(j.error || `Erreur ${r.status}`);
      showNotification?.('Passage lancé — le compte rendu se remplit ici.', 'success');
      setOuverts({});
      await charger();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setLancement(false); }
  };

  // Les gestes sur un test : mêmes routes que les actions du copilote
  // (sonde_scenario_statut) — l'écran ne fait rien qu'il ne puisse faire.
  const changerTest = async (t, statutVise) => {
    setTestBusy(`${t.id}:${statutVise}`);
    try {
      const r = await fetch(`${BASE}/sonde/scenarios/${t.id}`, { method: 'PUT', headers: { ...authH(), 'Content-Type': 'application/json' }, body: JSON.stringify({ statut: statutVise }) });
      await lireJson(r);
      showNotification?.(statutVise === 'adopte' ? 'Test adopté — ses échecs comptent désormais.'
        : statutVise === 'desactive' ? 'Test désactivé — il ne tourne plus.' : 'Test remis à l\'essai.', 'success');
      await chargerTests();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setTestBusy(null); }
  };
  const supprimerTest = async (t) => {
    const ok = await confirmErpia({
      title: 'Supprimer le test', message: `« ${t.titre} » ne sera plus rejoué par la sonde.`,
      detail: 'Ses résultats passés restent dans l\'historique des passages. Cette suppression est définitive.',
      confirmLabel: 'Supprimer', tone: 'danger',
    });
    if (!ok) return;
    setTestBusy(`${t.id}:supprime`);
    try {
      await lireJson(await fetch(`${BASE}/sonde/scenarios/${t.id}`, { method: 'DELETE', headers: authH() }));
      showNotification?.('Test supprimé.', 'success');
      await chargerTests();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setTestBusy(null); }
  };
  const essayerTest = async (t) => {
    setTestBusy(`${t.id}:essai`);
    setTestOuvert(t.id);
    try {
      const r = await fetch(`${BASE}/sonde/scenarios/essayer`, { method: 'POST', headers: { ...authH(), 'Content-Type': 'application/json' }, body: JSON.stringify({ id: t.id }) });
      const j = await lireJson(r);
      setEssais(e => ({ ...e, [t.id]: j.data }));
      charger();
    } catch (e) { setEssais(x => ({ ...x, [t.id]: { erreur: e.message } })); }
    finally { setTestBusy(null); }
  };

  if (vue === null) return <div className="sonde"><p className="sonde__note"><BI icon="arrow-repeat" /> Chargement…</p></div>;
  if (vue.absent) return <div className="sonde"><p className="sonde__note">La sonde arrive côté serveur : cet écran s'activera au prochain déploiement.</p></div>;
  if (vue.interdit) return <div className="sonde"><p className="sonde__note">La sonde est réservée à l'éditeur du produit.</p></div>;

  const passages = vue.passages || [];
  const enCours = detail?.statut === 'en_cours';
  // Les lignes du passage : le PLAN (tout ce qui doit tourner) complété de ce
  // qui a tourné. Un ancien passage sans plan se lit sur ses seuls résultats.
  const faitsParId = new Map((detail?.controles || []).map(c => [c.id, c]));
  const lignes = detail && !detail.erreur
    ? [
      ...(detail.plan || []).map(p => faitsParId.get(p.id) || { ...p, statut: 'attente' }),
      ...(detail.controles || []).filter(c => !(detail.plan || []).some(p => p.id === c.id)),
    ]
    : [];
  const faits = lignes.filter(c => c.statut !== 'attente' && c.statut !== 'en_cours').length;

  return (
    <div className="sonde">
      <header className="sonde__tete">
        <div>
          <h1 className="sonde__titre">Sonde</h1>
          <p className="sonde__sous-titre">
            Rejoue les gestes essentiels d'ERPIA dans un espace de test isolé — personne n'est contacté, et tout ce qu'elle crée est effacé à la fin.
          </p>
        </div>
        <button type="button" className="erp-btn erp-btn--primary" onClick={lancer} disabled={lancement || enCours}>
          {lancement || enCours ? <><BI icon="arrow-repeat" /> Passage en cours…</> : 'Lancer un passage'}
        </button>
      </header>

      {vue.erreur && <p className="sonde__erreur">{vue.erreur}</p>}

      {passages.length > 0 && (
        <section className="sonde__ruban-bloc" aria-label="Derniers passages">
          <div className="sonde__ruban">
            {[...passages].reverse().map(p => (
              <button key={p.id} type="button"
                className={`sonde__trait sonde__trait--${statut(p.statut).classe}${detail?.id === p.id ? ' is-actif' : ''}`}
                title={`#${p.id} · ${date(p.fin || p.debut)} · ${statut(p.statut).libelle}`}
                aria-label={`Passage ${p.id}, ${statut(p.statut).libelle}, ${date(p.fin || p.debut)}`}
                onClick={() => { setOuverts({}); chargerDetail(p.id); }} />
            ))}
          </div>
          <div className="sonde__ruban-legende">
            <span>{date(passages[passages.length - 1].debut)}</span>
            <span>{passages.length} passage{passages.length > 1 ? 's' : ''}</span>
            <span>{date(passages[0].debut)}</span>
          </div>
        </section>
      )}

      {passages.length === 0 && !vue.erreur && (
        <p className="sonde__note">
          La sonde n'a encore jamais tourné. « Lancer un passage » crée son espace de test la première fois, puis le réutilise.
        </p>
      )}

      {detail?.erreur && <p className="sonde__erreur">{detail.erreur}</p>}

      {detail && !detail.erreur && (
        <section className="erp-card sonde__passage">
          <div className="sonde__passage-tete">
            <span className={`sonde__verdict sonde__verdict--${statut(detail.statut).classe}`}>{statut(detail.statut).libelle}</span>
            <span className="erp-mono sonde__meta">
              #{detail.id} · {date(detail.debut)} · {DECLENCHEURS[detail.declencheur] || detail.declencheur}
              {detail.commit ? ` · ${String(detail.commit).slice(0, 7)}` : ''}
            </span>
          </div>
          {detail.resume && <p className="sonde__resume">{detail.resume}</p>}

          {enCours && (
            <p className="sonde__avancement">
              <span className="erp-mono">{faits}/{lignes.length}</span> contrôle{lignes.length > 1 ? 's' : ''} terminé{faits > 1 ? 's' : ''}
              {ETAPES[detail.etape] ? ` · ${ETAPES[detail.etape]}` : ''}
            </p>
          )}

          <ul className="sonde__controles">
            {lignes.map(c => {
              const s = statut(c.statut);
              const verifs = c.verifications || [];
              const pannes = verifs.filter(v => !v.ok);
              const attente = c.statut === 'attente';
              const tourne = c.statut === 'en_cours';
              const ouvert = !attente && (ouverts[c.id] ?? !!(tourne || s.classe === 'bad' || s.classe === 'warn'));
              return (
                <li key={c.id} className={`sonde__controle${attente ? ' is-attente' : ''}${tourne ? ' is-en-cours' : ''}`}>
                  <button type="button" className="sonde__controle-ligne" aria-expanded={attente ? undefined : ouvert}
                    disabled={attente} title={attente ? c.description || '' : undefined}
                    onClick={() => setOuverts(o => ({ ...o, [c.id]: !ouvert }))}>
                    {tourne
                      ? <i className="bi bi-arrow-repeat sonde__tourne" aria-hidden="true" />
                      : <span className={`sonde__pastille sonde__pastille--${s.classe}`} aria-hidden="true" />}
                    <span className="sonde__controle-nom">
                      {c.libelle}
                      {c.scenario && (
                        <span className="erp-chip sonde__chip">
                          {c.scenario.origine === 'copilote' ? 'test du copilote' : 'test ajouté'}
                          {c.scenario.statut === 'essai' ? ' · à l\'essai' : ''}
                        </span>
                      )}
                    </span>
                    {!attente && (
                      <span className="erp-mono sonde__controle-compte">{verifs.length - pannes.length}/{verifs.length}</span>
                    )}
                    <span className={`sonde__etat sonde__etat--${s.classe}`}>{s.libelle}</span>
                    {!attente && <BI icon={ouvert ? 'chevron-up' : 'chevron-down'} />}
                  </button>
                  {ouvert && (
                    <div className="sonde__controle-detail" aria-live={tourne ? 'polite' : undefined}>
                      {c.erreur && <p className="sonde__ligne sonde__ligne--bad">Interrompu : {c.erreur}</p>}
                      {/* En cours : les vérifications dans l'ordre où elles
                          tombent. Fini : les pannes d'abord. */}
                      {(tourne ? verifs : [...pannes, ...verifs.filter(v => v.ok)]).map((v, i) => (
                        <p key={`v${i}`} className={`sonde__ligne${v.ok ? '' : ' sonde__ligne--bad'}`}>
                          <BI icon={v.ok ? 'check2' : 'x-lg'} /> {v.message}
                          {!v.ok && v.detail && <span className="erp-mono sonde__detail">{JSON.stringify(v.detail)}</span>}
                        </p>
                      ))}
                      {(c.avertissements || []).map((a, i) => <p key={`a${i}`} className="sonde__ligne sonde__ligne--warn">{a}</p>)}
                      {tourne && <p className="sonde__ligne"><BI icon="three-dots" /> vérification suivante…</p>}
                      {!tourne && !verifs.length && !c.erreur && <p className="sonde__ligne">Aucune vérification enregistrée.</p>}
                      {(c.nettoyage || []).some(n => !n.ok) && (
                        <p className="sonde__ligne sonde__ligne--warn">
                          Nettoyage incomplet : {(c.nettoyage || []).filter(n => !n.ok).map(n => n.libelle).join(', ')} — le balayage final s'en est chargé.
                        </p>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {detail.balayage && (
            <p className={`erp-mono sonde__balayage${detail.balayage.persistant?.total ? ' sonde__balayage--bad' : ''}`}>
              {detail.balayage.efface?.total ?? 0} ligne{(detail.balayage.efface?.total ?? 0) > 1 ? 's' : ''} de test effacée{(detail.balayage.efface?.total ?? 0) > 1 ? 's' : ''}
              {' · '}{detail.balayage.persistant?.total ?? 0} restée{(detail.balayage.persistant?.total ?? 0) > 1 ? 's' : ''} en base
              {detail.balayage.avant?.total ? ` · ${detail.balayage.avant.total} ramassée${detail.balayage.avant.total > 1 ? 's' : ''} d'un passage interrompu` : ''}
            </p>
          )}
        </section>
      )}

      {tests && !tests.absent && (
        <section className="sonde__tests" aria-labelledby="sonde-tests-titre">
          <h2 id="sonde-tests-titre" className="sonde__couv-titre">Tests ajoutés</h2>
          <p className="sonde__couv-resume">
            Demandez-les au copilote : « ajoute à la sonde un test qui vérifie que… ». Il l'écrit avec ses propres actions,
            l'essaie dans l'espace de test, puis vous propose de l'enregistrer. Un test naît à l'essai : il tourne à chaque
            passage, mais ses échecs ne comptent qu'une fois adopté.
          </p>
          {tests.erreur && <p className="sonde__erreur">{tests.erreur}</p>}
          {Array.isArray(tests) && tests.length === 0 && (
            <p className="sonde__note">Aucun test ajouté pour l'instant.</p>
          )}
          {Array.isArray(tests) && tests.length > 0 && (
            <ul className="sonde__couv-liste">
              {tests.map(t => {
                const ouvert = testOuvert === t.id;
                const res = t.dernier_resultat;
                const essai = essais[t.id];
                const occupe = testBusy && testBusy.startsWith(`${t.id}:`);
                return (
                  <li key={t.id} className={`sonde__couv-module${t.statut === 'desactive' ? ' is-attente' : ''}`}>
                    <button type="button" className="sonde__test-ligne" aria-expanded={ouvert}
                      onClick={() => setTestOuvert(ouvert ? null : t.id)}>
                      <span className={`sonde__pastille sonde__pastille--${res ? statut(res.statut).classe : 'wait'}`} aria-hidden="true" />
                      <span className="sonde__controle-nom">
                        {t.titre}
                        {t.origine === 'copilote' && <span className="erp-chip sonde__chip">écrit par le copilote</span>}
                      </span>
                      <span className="erp-mono sonde__controle-compte">{(t.etapes || []).length} étape{(t.etapes || []).length > 1 ? 's' : ''}</span>
                      <span className={`sonde__etat${t.statut === 'adopte' ? '' : ' sonde__etat--wait'}`}>{ETATS_TEST[t.statut] || t.statut}</span>
                      <BI icon={ouvert ? 'chevron-up' : 'chevron-down'} />
                    </button>
                    {ouvert && (
                      <div className="sonde__controle-detail">
                        {t.description && <p className="sonde__ligne">{t.description}</p>}
                        <ol className="sonde__etapes">
                          {(t.etapes || []).map((e, i) => (
                            <li key={i} className="sonde__ligne">
                              {e.note || e.outil}
                              <span className="erp-mono sonde__detail">{e.outil}{e.echec_attendu ? ' · refus attendu' : ''}</span>
                            </li>
                          ))}
                        </ol>
                        {res && (
                          <p className="sonde__ligne">
                            Dernier résultat : <b>{statut(res.statut).libelle}</b> · {date(res.le)}
                          </p>
                        )}
                        {essai?.erreur && <p className="sonde__ligne sonde__ligne--bad">{essai.erreur}</p>}
                        {essai && !essai.erreur && (
                          <div className="sonde__essai" aria-live="polite">
                            <p className="sonde__ligne"><b>Essai : {statut(essai.statut).libelle}</b></p>
                            {(essai.verifications || []).map((v, i) => (
                              <p key={i} className={`sonde__ligne${v.ok ? '' : ' sonde__ligne--bad'}`}>
                                <BI icon={v.ok ? 'check2' : 'x-lg'} /> {v.message}
                                {!v.ok && v.detail && <span className="erp-mono sonde__detail">{JSON.stringify(v.detail)}</span>}
                              </p>
                            ))}
                          </div>
                        )}
                        <div className="sonde__test-actions">
                          <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" disabled={occupe || enCours} onClick={() => essayerTest(t)}>
                            {testBusy === `${t.id}:essai` ? <><BI icon="arrow-repeat" /> Essai en cours…</> : 'Essayer maintenant'}
                          </button>
                          {t.statut !== 'adopte' && (
                            <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" disabled={occupe} onClick={() => changerTest(t, 'adopte')}>Adopter</button>
                          )}
                          {t.statut === 'adopte' && (
                            <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" disabled={occupe} onClick={() => changerTest(t, 'essai')}>Remettre à l'essai</button>
                          )}
                          {t.statut !== 'desactive'
                            ? <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" disabled={occupe} onClick={() => changerTest(t, 'desactive')}>Désactiver</button>
                            : <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" disabled={occupe} onClick={() => changerTest(t, 'essai')}>Réactiver à l'essai</button>}
                          <button type="button" className="erp-btn erp-btn--danger erp-btn--sm" disabled={occupe} onClick={() => supprimerTest(t)}>Supprimer</button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}

      {couv && !couv.absent && (
        <section className="sonde__couv" aria-labelledby="sonde-couv-titre">
          <h2 id="sonde-couv-titre" className="sonde__couv-titre">Ce que la sonde teste — et ce qu'elle ne touche pas</h2>
          {couv.erreur && <p className="sonde__erreur">{couv.erreur}</p>}
          {!couv.erreur && !couv.passage && (
            <p className="sonde__note">La couverture se calcule sur un passage terminé : lancez-en un.</p>
          )}
          {!couv.erreur && couv.passage && (
            <>
              <p className="sonde__couv-resume">
                <span className="erp-mono">{couv.couvertes}/{couv.total}</span> actions du produit appelées par le passage #{couv.passage.id}.
                Les modules les moins éprouvés d'abord : c'est là qu'un nouveau test rapporte le plus.
              </p>
              <ul className="sonde__couv-liste">
                {(couvToutes ? couv.modules : couv.modules.slice(0, 10)).map(m => {
                  const ouvert = couvOuverte === m.module;
                  const manquantes = m.routes.filter(r => !r.couverte);
                  return (
                    <li key={m.module} className="sonde__couv-module">
                      <button type="button" className="sonde__couv-ligne" aria-expanded={ouvert}
                        onClick={() => setCouvOuverte(ouvert ? null : m.module)}>
                        <span className="sonde__couv-nom">{m.module}</span>
                        <span className="sonde__couv-barre" aria-hidden="true">
                          <span style={{ width: `${Math.round((m.couvertes / Math.max(1, m.total)) * 100)}%` }} />
                        </span>
                        <span className="erp-mono sonde__couv-compte">{m.couvertes}/{m.total}</span>
                        <span className={`sonde__couv-ecr${m.ecritures_manquantes ? ' is-manque' : ''}`}>
                          {m.ecritures_manquantes
                            ? `${m.ecritures_manquantes} écriture${m.ecritures_manquantes > 1 ? 's' : ''} jamais testée${m.ecritures_manquantes > 1 ? 's' : ''}`
                            : 'écritures testées'}
                        </span>
                        <BI icon={ouvert ? 'chevron-up' : 'chevron-down'} />
                      </button>
                      {ouvert && (
                        <div className="sonde__couv-detail">
                          {manquantes.length === 0 && <p className="sonde__ligne">Tout ce module est appelé par la sonde.</p>}
                          {manquantes.map(r => (
                            <p key={`${r.methode} ${r.chemin}`} className="sonde__ligne">
                              <span className="sonde__couv-type">{METHODES[r.methode] || r.methode.toLowerCase()}</span>
                              <span className="erp-mono">{r.chemin}</span>
                            </p>
                          ))}
                          {m.routes.some(r => r.couverte) && (
                            <p className="sonde__ligne sonde__couv-par">
                              Déjà testé par : {[...new Set(m.routes.flatMap(r => r.controles))].join(', ')}
                            </p>
                          )}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
              {couv.modules.length > 10 && (
                <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setCouvToutes(v => !v)}>
                  {couvToutes ? 'Montrer les 10 premiers' : `Montrer les ${couv.modules.length} modules`}
                </button>
              )}
            </>
          )}
        </section>
      )}
    </div>
  );
}
